/**
 * pricingEducationPlans.ts
 *
 * Builds the AI Bootcamp cards on /pricing from GET /checkout/catalog.
 *
 * WHY THIS EXISTS:
 * - Until 2026-10-14 the pricing page showed "See course pricing" for the live
 *   Bootcamp and a typed-in self-paced price, under the monthly plans.
 *   The Bootcamp is the product that brings in the most money per buyer, so
 *   Rob moved it above the plans and asked for real prices on it.
 * - Every price, payment plan, session count, start date, guarantee length and
 *   included Pro days comes from the catalog (product, product_plan, promotions
 *   and product.metadata). Nothing here hardcodes a number.
 *
 * NO FALLBACKS: when the catalog cannot supply a Bootcamp product or its prices,
 * the builder throws. The page then shows an unavailable state for the Bootcamp
 * section and no buy button, never a stale price.
 */
import { formatNextLiveClassDate, nextCohortStartFrom } from '../components/course/nextLiveClass';
import type { PricingPlan } from './PricingPageExperiment';

export const LIVE_BOOTCAMP_SLUG = 'ai-bootcamp';
export const SELF_PACED_BOOTCAMP_SLUG = 'ai-bootcamp-self-paced';
export const PRO_PLAN_SLUG = 'pro-plan';

type CatalogPlan = {
  planKey: string;
  label?: string | null;
  amount?: number | null;
  discountedAmount?: number | null;
  stripePriceId?: string | null;
  interval?: string | null;
  metadata?: { installments?: number } | null;
  checkoutAvailability?: { purchasable?: boolean; status?: string; message?: string | null };
  /** Set by Node when an active promotion discounts this plan (checkoutCatalog.service). */
  appliedPromotion?: CatalogPromotion | null;
};

type CatalogPromotion = {
  discountType?: 'percentage' | 'fixed_amount' | string;
  discountValue?: number | null;
  endDate?: string | null;
  duration?: 'once' | 'repeating' | 'forever' | string | null;
  durationInMonths?: number | null;
};

/** Bootcamp fields Node publishes in product.metadata (see Node checkoutCatalog.service). */
type BootcampCohort = {
  startsAt?: string | null;
  timezone?: string | null;
  sessionCount?: number | null;
};

type BootcampMetadata = {
  cohort?: BootcampCohort | null;
  includedProDays?: number | null;
  guaranteeDays?: number | null;
  priceAnchorCents?: number | null;
};

type CatalogProduct = {
  slug: string;
  name?: string | null;
  metadata?: BootcampMetadata | null;
  plans?: CatalogPlan[];
};

type Catalog = { products?: CatalogProduct[] } | null | undefined;

type EducationFeature = { label: string; detail?: string };

/** Copy the education tiers already own in publicPricingSource. */
export type EducationTierCopy = {
  id: string;
  name: string;
  tagline: string;
  bestFor?: string;
  cta: string;
  features: EducationFeature[];
};

/**
 * The price a course row shows on /pricing (Rob, 2026-10-21: "delete half
 * this text"). Every number comes from the catalog plans.
 *
 * Rob, 2026-10-22: a course with a payment plan leads with the monthly
 * payment ("5 payments of $697", big) and puts the pay-in-full price under it
 * ("or $2,999 in full", small). A one-payment course leads with its price.
 */
export type CoursePriceSummary = {
  /**
   * Headline charge in dollars, after any promotion: one payment of the
   * longest payment plan when there is one, otherwise the one-time price.
   */
  price: number;
  /** The same charge before a promotion or anchor lowered it, shown struck through. */
  originalPrice?: number;
  /** Number of payments when the headline is a payment plan (always more than 1). */
  installments?: number;
  /** Pay-in-full charge in dollars, after any promotion. Set with installments. */
  fullPrice?: number;
};

export type EducationPricingPlan = PricingPlan & {
  type: 'course';
  /** "one-time": the plan card price suffix. The Bootcamp row uses priceSummary. */
  priceSuffix: string;
  /** Short facts read from catalog metadata, shown before the copy bullets. */
  facts: string[];
  checkoutClosedMessage?: string;
  /** "33% off every payment until Fri, Oct 23", from the catalog promotion. */
  promotionNote?: string;
  /**
   * Price line for the compact course row. Absent when nothing can be bought
   * (a closed live cohort shows its next class instead of a price).
   */
  priceSummary?: CoursePriceSummary;
  /** Small line under the name, for example "by BotSpot". */
  byline?: string;
  /** One sentence shown under all course rows, built from catalog prices. */
  footnote?: string;
  /**
   * The course page this row sends buyers to. Research 2026-10-21
   * (MarketingManager/docs/investigations/2026-10-21_pricing-page-bootcamp-cta-research.md):
   * no Bootcamp checkout ever started on /pricing; buyers read the course page
   * first, so the pricing page links there instead of starting checkout.
   */
  coursePath: string;
};

const usd = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: cents % 100 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;

/**
 * Per-payment charge, list amount and total for one catalog plan. The same
 * arithmetic the course page uses: the discounted amount wins when present, and
 * an installment plan's total is the per-payment charge times its payments.
 */
function planView(plan: CatalogPlan) {
  const amount = Number(plan.amount);
  const discounted =
    Number.isSafeInteger(plan.discountedAmount) && Number(plan.discountedAmount) < amount
      ? Number(plan.discountedAmount)
      : null;
  const charge = discounted ?? amount;
  const installments = positiveInt(plan.metadata?.installments) ?? 1;
  return {
    installments,
    amount: charge,
    originalAmount: amount,
    hasDiscount: discounted !== null,
    totalDisplay: usd(charge * installments),
  };
}

/** "Fri, Oct 23": promotions end at 11:59 PM Eastern, so the date reads in Eastern. */
function promotionEndLabel(endDate: string | null | undefined): string | null {
  const end = endDate ? new Date(endDate) : null;
  if (!end || Number.isNaN(end.getTime())) return null;
  return end.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'America/New_York',
  });
}

/**
 * One line naming the promotion the catalog applied, or nothing. Says "every
 * payment" only when the coupon really lasts for every installment.
 */
export function describePromotion(
  plans: CatalogPlan[],
  formatCents: (cents: number) => string = usd,
): string | undefined {
  const discounted = plans.filter(
    (plan) =>
      plan.appliedPromotion &&
      Number.isSafeInteger(plan.discountedAmount) &&
      Number(plan.discountedAmount) < Number(plan.amount),
  );
  const promotion = discounted[0]?.appliedPromotion;
  if (!promotion) return undefined;
  const value = Number(promotion.discountValue);
  if (!(value > 0)) return undefined;
  const amount =
    promotion.discountType === 'percentage' ? `${value}% off` : `${formatCents(value)} off`;
  const maxInstallments = Math.max(
    ...discounted.map((plan) => positiveInt(plan.metadata?.installments) ?? 1),
  );
  const coversEveryPayment =
    maxInstallments > 1 &&
    (promotion.duration === 'forever' ||
      (promotion.duration === 'repeating' &&
        (positiveInt(promotion.durationInMonths) ?? 0) >= maxInstallments));
  const scope = coversEveryPayment ? ' every payment' : '';
  const end = promotionEndLabel(promotion.endDate);
  return `${amount}${scope}${end ? ` until ${end}` : ''}`;
}

const positiveInt = (value: number | null | undefined): number | null => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
};

function findProduct(catalog: Catalog, slug: string): CatalogProduct {
  const product = (catalog?.products || []).find((p) => p?.slug === slug);
  if (!product) throw new Error(`Commercial catalog is missing ${slug}`);
  return product;
}

/** "Wed, Oct 28" in the cohort's own time zone. */
function cohortStartLabel(cohort: BootcampCohort | null | undefined): string | null {
  const startsAt = cohort?.startsAt ? new Date(cohort.startsAt) : null;
  if (!startsAt || Number.isNaN(startsAt.getTime())) return null;
  const timeZone = cohort?.timezone || 'America/New_York';
  return startsAt.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone,
  });
}

/** Copy bullets that restate a catalog fact are replaced by the catalog fact. */
const CATALOG_OWNED_COPY = /launch price|days of pro access|money-back/i;

// These buttons open the course page, where buyers read the details and pay.
const COURSE_CTA = {
  live: 'See the Bootcamp',
  selfPaced: 'See the Membership',
} as const;

export const LIVE_BOOTCAMP_PATH = '/courses/ai-trading-bootcamp';
export const SELF_PACED_BOOTCAMP_PATH = '/courses/ai-trading-bootcamp/self-paced';

function copyFeatures(copy: EducationTierCopy) {
  return copy.features.filter((feature) => !CATALOG_OWNED_COPY.test(feature.label));
}

export function buildLiveBootcampPlan(
  catalog: Catalog,
  copy: EducationTierCopy,
  nextLiveStart: Date | null = null,
): EducationPricingPlan {
  const product = findProduct(catalog, LIVE_BOOTCAMP_SLUG);
  const purchasable = (product.plans || []).filter(
    (plan) => plan.stripePriceId && Number.isSafeInteger(plan.amount),
  );
  const onSale = purchasable.filter((plan) => plan.checkoutAvailability?.purchasable !== false);
  if (!onSale.length) return closedLiveBootcampPlan(product, copy, nextLiveStart);

  // Same per-payment and total math the course page uses (useCourseCatalog).
  const plans = onSale.map((plan) => ({ raw: plan, view: planView(plan) }));
  const byInstallments = [...plans].sort((a, b) => a.view.installments - b.view.installments);
  const inFull = byInstallments.find((p) => p.view.installments === 1);
  const mostInstallments = byInstallments[byInstallments.length - 1];
  const hasPaymentPlan = mostInstallments.view.installments > 1;

  const metadata: BootcampMetadata = product.metadata || {};
  const cohort = metadata.cohort;
  const sessionCount = positiveInt(cohort?.sessionCount);
  const startLabel = cohortStartLabel(cohort);
  const guaranteeDays = positiveInt(metadata.guaranteeDays);

  const facts: string[] = [];
  if (sessionCount && startLabel)
    facts.push(`${sessionCount} live sessions, starting ${startLabel}`);
  else if (startLabel) facts.push(`Next class starts ${startLabel}`);
  // Pro days stay on the course page; the pricing row keeps two short facts.
  if (guaranteeDays) facts.push(`${guaranteeDays}-day money-back guarantee`);
  const fullPrice = inFull ? inFull.view.amount / 100 : undefined;

  return {
    id: copy.id,
    type: 'course',
    name: copy.name,
    tagline: copy.tagline,
    bestFor: copy.bestFor,
    cta: COURSE_CTA.live,
    features: copyFeatures(copy),
    facts,
    price: fullPrice ?? (mostInstallments.view.amount * mostInstallments.view.installments) / 100,
    originalPrice: inFull?.view.hasDiscount ? inFull.view.originalAmount / 100 : undefined,
    priceSuffix: 'one-time',
    productSlug: product.slug,
    planKey: (inFull ?? mostInstallments).raw.planKey,
    promotionNote: describePromotion(onSale),
    priceSummary: hasPaymentPlan
      ? {
          price: mostInstallments.view.amount / 100,
          originalPrice: mostInstallments.view.hasDiscount
            ? mostInstallments.view.originalAmount / 100
            : undefined,
          installments: mostInstallments.view.installments,
          fullPrice,
        }
      : {
          price: mostInstallments.view.amount / 100,
          originalPrice: mostInstallments.view.hasDiscount
            ? mostInstallments.view.originalAmount / 100
            : undefined,
        },
    coursePath: LIVE_BOOTCAMP_PATH,
  };
}

/**
 * Rob, 2026-10-28: when no live cohort can be bought, the live row shows no
 * price. It says when the next class starts (catalog cohort metadata, only if
 * that start is still ahead) or that dates are coming soon, and still links to
 * the live page.
 */
function closedLiveBootcampPlan(
  product: CatalogProduct,
  copy: EducationTierCopy,
  nextLiveStart: Date | null,
) {
  // Session rows (bootcamp-data) first, then catalog cohort metadata. Only a
  // start still ahead counts as the next class.
  const cohort = product.metadata?.cohort;
  const metadataStart = cohort?.startsAt ? new Date(cohort.startsAt) : null;
  const next = nextCohortStartFrom([
    ...(nextLiveStart ? [nextLiveStart] : []),
    ...(metadataStart ? [metadataStart] : []),
  ]);
  const startLabel = formatNextLiveClassDate(next);
  return closedEducationPlan(
    product,
    copy,
    [startLabel ? `Next live class: ${startLabel}` : 'Next live class: dates coming soon'],
    LIVE_BOOTCAMP_PATH,
    COURSE_CTA.live,
  );
}

function closedEducationPlan(
  product: CatalogProduct,
  copy: EducationTierCopy,
  facts: string[],
  coursePath: string,
  cta: string,
): EducationPricingPlan {
  return {
    id: copy.id,
    type: 'course' as const,
    name: copy.name,
    tagline: copy.tagline,
    bestFor: copy.bestFor,
    cta,
    features: copyFeatures(copy),
    facts,
    priceSuffix: 'one-time',
    productSlug: product.slug,
    checkoutClosedMessage: 'Not on sale right now.',
    coursePath,
  };
}

/** "5 payments of $219 gets you ... Pro alone is $199 every month." From catalog prices. */
function membershipComparisonNote(
  catalog: Catalog,
  installments: number | null,
  monthlyCents: number | null,
): string | undefined {
  const pro = (catalog?.products || []).find((p) => p?.slug === PRO_PLAN_SLUG);
  const proMonthly = (pro?.plans || []).find(
    (plan) => plan.interval === 'month' && Number.isSafeInteger(plan.amount),
  );
  if (!proMonthly || !installments || !monthlyCents) return undefined;
  return `${installments} payments of ${usd(monthlyCents)} gets you the Bootcamp, Tuesdays and a full year of Pro. Pro alone is ${usd(Number(proMonthly.amount))} every month.`;
}

export function buildSelfPacedBootcampPlan(
  catalog: Catalog,
  copy: EducationTierCopy,
): EducationPricingPlan {
  const product = findProduct(catalog, SELF_PACED_BOOTCAMP_SLUG);
  const sellable = (product.plans || [])
    .filter((candidate) => candidate.stripePriceId && Number.isSafeInteger(candidate.amount))
    .map((candidate) => ({ raw: candidate, view: planView(candidate) }))
    .sort((a, b) => a.view.installments - b.view.installments);
  if (!sellable.length) throw new Error('Commercial catalog has no self-paced Bootcamp plan');
  const onSale = sellable.filter((entry) => entry.raw.checkoutAvailability?.purchasable !== false);
  if (!onSale.length) {
    const proDays = positiveInt(product.metadata?.includedProDays);
    return closedEducationPlan(
      product,
      { ...copy, name: product.name || copy.name },
      proDays ? [`${proDays} days of BotSpot Pro included`] : [],
      SELF_PACED_BOOTCAMP_PATH,
      COURSE_CTA.selfPaced,
    );
  }
  // The Membership (Rob, 2026-10-28) sells pay in full and a monthly plan.
  // The monthly payment leads the card, the way the live Bootcamp does.
  const inFull = onSale.find((entry) => entry.view.installments === 1);
  const mostInstallments = onSale[onSale.length - 1];
  const hasPaymentPlan = mostInstallments.view.installments > 1;
  const plan = (inFull ?? mostInstallments).raw;

  const view = (inFull ?? mostInstallments).view;
  const metadata: BootcampMetadata = product.metadata || {};
  const anchorCents = positiveInt(metadata.priceAnchorCents);
  const paymentCount = inFull ? 1 : view.installments;
  const chargeCents = Number(view.amount) * paymentCount;
  // The list price is the plan's own pre-promotion amount when a promotion
  // applies, otherwise the product's recorded anchor price.
  const listCents = view.hasDiscount
    ? Number(view.originalAmount) * paymentCount
    : inFull && anchorCents && anchorCents > chargeCents
      ? anchorCents
      : null;
  const proDays = positiveInt(metadata.includedProDays);

  const facts: string[] = [];
  if (proDays) facts.push(`${proDays} days of BotSpot Pro included`);

  return {
    id: copy.id,
    type: 'course',
    // The Membership's name lives in the catalog so a rename needs no deploy.
    name: product.name || copy.name,
    byline: 'by BotSpot',
    footnote: hasPaymentPlan
      ? membershipComparisonNote(
          catalog,
          mostInstallments.view.installments,
          Number(mostInstallments.view.amount),
        )
      : undefined,
    tagline: copy.tagline,
    bestFor: copy.bestFor,
    cta: COURSE_CTA.selfPaced,
    features: copyFeatures(copy),
    facts,
    price: chargeCents / 100,
    originalPrice: listCents ? listCents / 100 : undefined,
    discountPercent: listCents ? Math.round((1 - chargeCents / listCents) * 100) : undefined,
    priceSuffix: 'one-time',
    promotionNote: describePromotion(onSale.map((entry) => entry.raw)),
    productSlug: product.slug,
    planKey: plan.planKey,
    priceSummary: hasPaymentPlan
      ? {
          price: mostInstallments.view.amount / 100,
          originalPrice: mostInstallments.view.hasDiscount
            ? mostInstallments.view.originalAmount / 100
            : undefined,
          installments: mostInstallments.view.installments,
          fullPrice: inFull ? chargeCents / 100 : undefined,
        }
      : {
          price: chargeCents / 100,
          originalPrice: listCents ? listCents / 100 : undefined,
        },
    coursePath: SELF_PACED_BOOTCAMP_PATH,
  };
}

/**
 * Membership first, then the live Bootcamp (Rob, 2026-10-28: the yearly
 * Membership is the main offer). Throws when either product is missing from
 * the catalog; callers render the Bootcamp unavailable state.
 */
export function buildEducationPricingPlans(
  catalog: Catalog,
  copies: EducationTierCopy[],
  { nextLiveStart = null }: { nextLiveStart?: Date | null } = {},
): EducationPricingPlan[] {
  const live = copies.find((c) => c.id === 'bootcamp-live');
  const selfPaced = copies.find((c) => c.id === 'bootcamp-self-paced');
  if (!live || !selfPaced) throw new Error('Bootcamp pricing copy is missing');
  return [
    buildSelfPacedBootcampPlan(catalog, selfPaced),
    buildLiveBootcampPlan(catalog, live, nextLiveStart),
  ];
}
